// Binary output 0x02 is always supported; 0x05 gzip is requested only when
// the browser can decode it. Mixed frames must be parsed in arrival order.
const MAX_OUTPUT_BYTES = 256 * 1024;

export function terminalSocketUrl(path: string, sessionId: string): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const url = new URL(`${proto}://${location.host}${path}`);
  url.searchParams.set("session_id", sessionId);
  if (typeof DecompressionStream === "function") url.searchParams.set("compression", "gzip");
  url.searchParams.set("flow", "ack-v1");
  url.searchParams.set("heartbeat", "ping-v1");
  return url.toString();
}

export function terminalOutput(
  write: (bytes: Uint8Array) => void | Promise<void>,
  onError: () => void,
  sendControl?: (frame: Uint8Array) => void,
) {
  let queue = Promise.resolve();
  let disposed = false;
  let flow = false;
  let sequence = 0;
  let cancel: () => void = () => {};
  const cancelled = new Promise<void>((resolve) => {
    cancel = resolve;
  });
  return {
    push(data: unknown) {
      if (disposed || !(data instanceof ArrayBuffer)) return;
      const frame = new Uint8Array(data);
      queue = queue
        .then(async () => {
          if (disposed || !frame.length) return;
          if (frame[0] === 0x04 && frame.length === 2 && frame[1] === 1) {
            flow = true;
            sequence = 0;
            return;
          }
          let decoded: Uint8Array | undefined;
          if (frame[0] === 0x02) decoded = frame.subarray(1);
          else if (frame[0] === 0x05) {
            const stream = new Blob([frame.subarray(1)])
              .stream()
              .pipeThrough(new DecompressionStream("gzip"));
            const reader = stream.getReader();
            const chunks: Uint8Array[] = [];
            let size = 0;
            for (;;) {
              const { value, done } = await reader.read();
              if (done) break;
              size += value.length;
              if (size > MAX_OUTPUT_BYTES) {
                await reader.cancel();
                throw new Error("oversized terminal output");
              }
              chunks.push(value);
            }
            const bytes = new Uint8Array(size);
            let offset = 0;
            for (const chunk of chunks) {
              bytes.set(chunk, offset);
              offset += chunk.length;
            }
            decoded = bytes;
          }
          if (decoded && !disposed) {
            await Promise.race([write(decoded), cancelled]);
            if (!disposed && flow && sendControl) {
              sequence = (sequence + 1) >>> 0;
              const ack = new Uint8Array(5);
              ack[0] = 0x04;
              new DataView(ack.buffer).setUint32(1, sequence, false);
              sendControl(ack);
            }
          }
        })
        .catch(() => {
          if (!disposed) {
            disposed = true;
            onError();
          }
        });
    },
    drain: () => queue,
    dispose: () => {
      disposed = true;
      cancel();
    },
  };
}
